import { captureEntryUtm } from '@/libs/analytics/entryUtm'

const IN_APP_OVERRIDE_KEY = 'debug:inAppBrowser'
const WHATSAPP_ENTRY_KEY = 'inAppBrowser:whatsappEntry'

// iOS WhatsApp opens links in SFSafariViewController, whose user agent is
// byte-identical to Safari's — undetectable by UA sniffing. But every WhatsApp
// entry comes from a link we authored, tagged by the outreach UTM taxonomy
// with `utm_medium=whatsapp` (utm_source is always `upapp`; the channel lives
// in the medium — see up-api engagement/domain/services/utm.py), so the
// arrival itself is the signal. Persist it in sessionStorage immediately
// (at module load, before the router can strip query params) so it survives
// in-tab navigation and refreshes for the life of the in-app browser tab.
const urlSaysWhatsApp = (): boolean =>
  new URLSearchParams(window.location.search)
    .get('utm_medium')
    ?.toLowerCase() === 'whatsapp'

// Exported so the app entry (src/index.tsx) can run it eagerly: this module
// otherwise lives in a lazily-loaded route chunk, which only evaluates after
// login has already replaced the URL and dropped the utm params.
export const captureWhatsAppEntry = () => {
  // Snapshot utm_* first — the strip below (and the auth redirect later)
  // destroys the only copy of the attribution GA needs.
  captureEntryUtm()
  try {
    if (urlSaysWhatsApp()) {
      sessionStorage.setItem(WHATSAPP_ENTRY_KEY, '1')
      // Strip utm_medium once stored, so "Open in Browser" / copied URLs don't
      // carry the marker into a real browser where saving actually works.
      // Other utm params stay — analytics still reads them.
      const url = new URL(window.location.href)
      url.searchParams.delete('utm_medium')
      window.history.replaceState(window.history.state, '', url)
    }
  } catch {
    // private mode / storage blocked — UA detection still covers Android
  }
}
captureWhatsAppEntry()

const hasWhatsAppEntry = (): boolean => {
  try {
    if (sessionStorage.getItem(WHATSAPP_ENTRY_KEY) === '1') return true
    // Fallback for when the boot-time capture couldn't store the marker
    // (private mode / storage blocked): read the live URL. Read-only — this
    // runs during React renders, so no storage writes or history mutations.
    return urlSaysWhatsApp()
  } catch {
    return false
  }
}

// Real in-app browsers are painful to reach for testing — WhatsApp in particular
// can only be opened by messaging yourself a link, which can't point at localhost.
// Outside production, `?inApp=1` forces detection on and sticks for the tab
// (so it survives navigation once the param is gone); `?inApp=0` clears it.
const getInAppOverride = (): boolean | null => {
  if (process.env.REACT_APP_ENVIRONMENT === 'production') return null
  try {
    const param = new URLSearchParams(window.location.search).get('inApp')
    if (param === '1' || param === 'true') {
      sessionStorage.setItem(IN_APP_OVERRIDE_KEY, '1')
      return true
    }
    if (param === '0' || param === 'false') {
      sessionStorage.removeItem(IN_APP_OVERRIDE_KEY)
      sessionStorage.removeItem(WHATSAPP_ENTRY_KEY)
      return false
    }
    return sessionStorage.getItem(IN_APP_OVERRIDE_KEY) === '1' ? true : null
  } catch {
    return null // private mode / storage blocked — fall back to UA sniffing
  }
}

// Best-effort detection for in-app browsers / WebViews. We can't reliably
// force-open Safari/Chrome from the web, so downloads and Wallet handoff are
// unsupported here — callers either hide the feature or show instructions.
// Detection is two-pronged: UA tokens catch Android WebViews and apps that
// announce themselves (Instagram, FB, …); the WhatsApp entry marker catches
// iOS WhatsApp, whose UA is indistinguishable from Safari.
export const getIsInAppBrowser = (): boolean => {
  const override = getInAppOverride()
  if (override !== null) return override

  if (hasWhatsAppEntry()) return true

  const ua = navigator.userAgent || ''
  const isAndroidWebView = /Android/i.test(ua) && /\bwv\b/i.test(ua)
  const isKnownInApp =
    /(FBAN|FBAV|FBIOS|FB_IAB|Instagram|WhatsApp|Line|Snapchat|TikTok|Twitter|LinkedInApp|Pinterest|GSA)/i.test(
      ua,
    )
  return isAndroidWebView || isKnownInApp
}
